import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import {Routes, Route, useLocation, Navigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useMemo, useEffect, useState} from "react";

import HomePage from "./pages/HomePage";
import HomePageWithReview from "./pages/HomePageWithReview";
import AboutPage from "./pages/AboutPage";
import TermsOfServicePage from "./pages/TermsOfService";
import PrivacyPolicyPage from "./pages/PrivacyPolicy";
import CookiePolicyPage from "./pages/CookiePolicy";

import HowItWorksPage from "./pages/HowItWorksPage";
import LoginPage from "./pages/LoginPage";
import ContractorDashboard from "./pages/ContractorDashboard";
import CommentsListingPage from "./pages/CommentsListingPage";
import AdminDashboard from "./pages/AdminDashboard";
import EmailVerifiedPage from "./pages/EmailVerifiedPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import {admin_role_id, contractor_role_id, super_admin_role_id} from "./constant";
import ReviewsListingPage from "./pages/ReviewsListingPage";
import ReviewsListingPageBackup from "./pages/ReviewsListingPage-back-up";

import {sweetAlerts} from "../src/utils/sweet-alerts";
import {isTokenExpired, getTimeUntilExpiration} from "./utils/tokenUtils";

import {palette} from "./theme/palette";
import {shadows} from "./theme/shadows";
import {overrides} from "./theme/overrides";
import {typography} from "./theme/typography";
import {customShadows} from "./theme/custom-shadows";

import {createTheme, ThemeProvider as MUIThemeProvider} from "@mui/material/styles";
import {GetBusinessVerifiedService} from "./services/authUpdate";
import { navigateToNextWithSync } from "./utils/syncUtils";

function App() {
	const location = useLocation();
	const dispatch = useDispatch();
	const [width, setWidth] = useState(window.innerWidth);

	const [roleId, setRoleId] = useState(() => {
		const token = localStorage.getItem("auth_token");
		const role = localStorage.getItem("role");
		// Only return role if we have a valid token
		return token ? role : null;
	});

	const [isDisable, setIsDisable] = useState(true);

	const [isLoading, setIsLoading] = useState(true);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const {error, message} = useSelector((state) => state.common);

	const memoizedValue = useMemo(
		() => ({
			palette: palette(),
			typography,
			shadows: shadows(),
			customShadows: customShadows(),
			shape: {borderRadius: 8},
		}),
		[],
	);
	const theme = createTheme(memoizedValue);

	theme.components = overrides(theme);

	// Function to handle logout
	const handleLogout = (showMessage = true) => {
		if (showMessage) {
			sweetAlerts("error", "Your login has been completed by the team, log in again");
		}

		localStorage.removeItem("auth_token");
		localStorage.removeItem("role");
		localStorage.removeItem("userDetails");
		dispatch({type: "USER_REMOVE"});
		setRoleId(null);

		// Redirect after showing message
		setTimeout(
			() => {
				// window.location.href = HOME_URL + "/login";
				window.location.href = "/login";
			},
			showMessage ? 2000 : 0,
		);
	};

	useEffect(() => {
		const token = localStorage.getItem("auth_token");
		const role = localStorage.getItem("role");

		// Only check token expiration if we have a token and we're not on login page
		if (token && location.pathname !== "/login") {
			const isExpired = isTokenExpired(token);
			if (isExpired) {
				handleLogout();
				return;
			}
		}

		// Set role based on token
		if (token) {
			setRoleId(role);
		} else {
			setRoleId(null);
		}

		setIsLoading(false);
		setIsInitialLoad(false);
	}, [location]);

	useEffect(() => {
		setTimeout(() => {
			dispatch({type: "FETCH_START"});
		}, 2200);
	}, [message, error]);

	// Token expiration check every 5 minutes
	useEffect(() => {
		const token = localStorage.getItem("auth_token");
		if (!token) return;

		const checkTokenExpiration = () => {
			// Only check if we're not on login page and we have a token
			if (location.pathname !== "/login" && isTokenExpired(token)) {
				handleLogout();
			}
		};

		// Don't check immediately on mount, wait a bit
		const initialTimeout = setTimeout(checkTokenExpiration, 2000);

		// Set up interval to check every 5 minutes
		const interval = setInterval(checkTokenExpiration, 5 * 60 * 1000);

		return () => {
			clearTimeout(initialTimeout);
			clearInterval(interval);
		};
	}, [location.pathname]);

	// const getAction = async () => {
	// 	const response = await GetBusinessVerifiedService();

	// 	if (response.status) {
	// 		const isContractor = response.data.role === "contractor";
	// 		const isVerified = Boolean(response?.data?.verified);

	// 		// if (isContractor && !isVerified && location.pathname !== "/contractor-dashboard/business-verified") {
	// 		// 	window.location.href = "/contractor-dashboard/business-verified";
	// 		// 	return;
	// 		// }

	// 		// Enable navigation/state once verification has been checked
	// 		setIsDisable(false);
	// 	}
	// };

	// useEffect(() => {
	// 	const token = localStorage.getItem("auth_token");
	// 	if (!token) return;
	// 	getAction();
	// }, [location.pathname]);

	const getAction = async () => {
		const token = localStorage.getItem("auth_token");
		if (!token) return;

		if (location.pathname === "/") {
			return;
		}

		const response = await GetBusinessVerifiedService();

		if (response.status) {
			const isContractor = response.data.role === "contractor";
			const isVerified = Boolean(response?.data?.verified);
			const trusted = Boolean(response?.data?.trusted);

			const shouldDisableNav = response.data.role === "contractor" && !response?.data?.verified;

			setIsDisable(shouldDisableNav);

			if (isContractor && !isVerified && location.pathname !== "/contractor-dashboard/business-verified") {
				window.location.href = "/contractor-dashboard/business-verified";
				return;
			} else {
				localStorage.setItem("verified", isVerified);
				localStorage.setItem("trusted", trusted);
			}
		}
	};

	useEffect(() => {
		const token = localStorage.getItem("auth_token");
		if (!token) return;
		getAction();
	}, [location.pathname]);

	useEffect(() => {
		const handleResize = () => setWidth(window.innerWidth);

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return (
		<>
			{error ? sweetAlerts("error", error) : ""}
			{message ? sweetAlerts("success", message) : ""}

			<MUIThemeProvider theme={theme}>
				<Routes>
					<Route path="/" element={<LoginPage />} />
					{/* <Route path="/" element={<HomePage />} /> */}
					{/* <Route path="/check-address" element={<HomePage />} /> */}
					{/* <Route path="/home-with-review" element={<HomePageWithReview />} /> */}
					{/* <Route path="/about" element={<AboutPage />} /> */}

					{/* <Route path="/terms-of-service" element={<TermsOfServicePage />} /> */}
					{/* <Route path="/privacy-policy" element={<PrivacyPolicyPage />} /> */}
					{/* <Route path="/cookie-policy" element={<CookiePolicyPage />} /> */}

					{/* <Route path="/how-it-works" element={<HowItWorksPage />} /> */}
					<Route path="/login" element={<LoginPage />} />
					<Route path="/signup" element={<LoginPage />} />
					<Route path="/verify-email" element={<EmailVerifiedPage />} />
					<Route path="/unsubscribe" element={<UnsubscribePage />} />
					<Route path="/reviews" element={width >= 990 ? <ReviewsListingPageBackup /> : <ReviewsListingPage />} />
					<Route path="/comments" element={<CommentsListingPage />} />
					{/* Contractor routes */}

					{(() => {
						const shouldShow = roleId == contractor_role_id;
						return (
							shouldShow && (
								<>
									<Route path="/contractor-dashboard/*" element={<ContractorDashboard />} />
									<Route path="/admin-dashboard/*" element={<Navigate to="/contractor-dashboard" replace />} />
								</>
							)
						);
					})()}

					{/* Admin routes */}
					{(() => {
						const shouldShow = roleId == super_admin_role_id || roleId == admin_role_id;
						return (
							shouldShow && (
								<>
									<Route path="/admin-dashboard/*" element={<AdminDashboard />} />
									<Route path="/contractor-dashboard/*" element={<Navigate to="/admin-dashboard" replace />} />
								</>
							)
						);
					})()}

					{/* Catch-all for unauthenticated users - only redirect if we're sure there's no role and not initial load */}
					{(() => {
						const shouldRedirect = !isLoading && !isInitialLoad && roleId === null;
						return shouldRedirect && <Route path="*" element={<Navigate to="/" replace />} />;
					})()}
				</Routes>
			</MUIThemeProvider>
		</>
	);
}

function RootRedirect() {
	useEffect(() => {
		navigateToNextWithSync("/");
	}, []);
	return null;
}

export default App;
